import { ReactNode } from "react";
import { Box, Spinner, Text, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";

import {
    getClaimsInfoByIdQueryOptions,
    getCompanyAssignedSubclaimsOfClaimQueryOptions
} from "@/api/queryOptions/claimsApiQueryOptions";
import { Radio, RadioGroup } from "@digital-services-gard-as/anchor-react";
import { pickSubClaim } from "@/utils/subClaimUtils";

type SubclaimsListProps = {
    claimRef: string;
    defaultValue: string;
    onChange: (subClaimRef: string) => void;
    crmId?: string;
};

export const SubclaimsList = ({ claimRef, defaultValue, onChange, crmId }: SubclaimsListProps) => {
    const { data: claim, isLoading: isLoadingClaim } = useQuery(getClaimsInfoByIdQueryOptions(claimRef));
    const { data: assignedRefs, isLoading: isLoadingAssigned } = useQuery(
        getCompanyAssignedSubclaimsOfClaimQueryOptions(claimRef, crmId)
    );

    const subClaims = claim?.subClaims;
    // Keep the spinner while a subclaim is about to be picked for the user, so the radios never
    // flash unselected. When the choice is theirs to make, show the list as soon as data is in.
    const isAutoSelecting = !defaultValue && Boolean(pickSubClaim(subClaims, assignedRefs));

    if (isLoadingClaim || isLoadingAssigned || isAutoSelecting) {
        return (
            <LabelledBox>
                <Spinner />
            </LabelledBox>
        );
    }

    if (!subClaims?.length) {
        return (
            <LabelledBox>
                <Text color="red">{t("errorMessages:subClaimsModal.noSubclaims")}</Text>
            </LabelledBox>
        );
    }

    if (subClaims.length === 1) {
        return (
            <LabelledBox>
                <Text data-testid="oneSubclaimOption">{subClaims[0].gardSubClaimRef}</Text>
            </LabelledBox>
        );
    }

    return (
        <Box maxH="13rem" overflowY="auto" p="0.75rem">
            <RadioGroup value={defaultValue} onChange={onChange} data-testid="subclaimsList" legend={t(LABEL_KEY)}>
                <VStack alignItems="flex-start" gap={2}>
                    {subClaims.map((subClaim, index) => (
                        <Radio
                            key={subClaim.gardSubClaimRef}
                            value={subClaim.gardSubClaimRef}
                            data-testid="subclaimOption"
                            size="sm"
                        >
                            {index + 1} – {subClaim.description ?? subClaim.gardSubClaimRef}
                        </Radio>
                    ))}
                </VStack>
            </RadioGroup>
        </Box>
    );
};

const LABEL_KEY = "invoices:createInvoice.generalInfo.subClaimNo";

const LabelledBox = ({ children }: { children: ReactNode }) => (
    <Box>
        <Text mb="0.5rem" display="block" asChild>
            <b>{t(LABEL_KEY)}</b>
        </Text>
        {children}
    </Box>
);
